define(function (require, exports, module) {
    'use strict';

    var requestMicrotask = require('./requestMicrotask');

    /**
     * Examples of using the AsyncIterator:
     *
     * 1. Get the latest result:
     *
     * | new AsyncIterator([1, 2, 3], function (value, index, array, next) {
     * |     next(value * 2);
     * | }).then(function (result) {
     * |     console.log(result); // → 6;
     * | });
     *
     * 2. Reduce to new array (thisObject):
     *
     * | new AsyncIterator([1, 2, 3], function (value, index, array, next) {
     * |     var result = this;
     * |
     * |     result[index] = value * 2;
     * |     next(result);
     * |
     * | }, []).then(function (result) {
     * |     console.log(result); // → [2, 4, 6];
     * | });
     *
     * 3. Finish without reaching the end:
     *
     * | new AsyncIterator([1, NaN, 3], function (value, index, array, next, finish) {
     * |     if (isNaN(value)) {
     * |         finish(index);
     * |     } else {
     * |         next();
     * |     }
     * | }).then(function (result) {
     * |     console.log(result); // → 1;
     * | });
     *
     * 4. Break interations:
     *
     * | new AsyncIterator([1, NaN, 3], function (value, index, array, next) {
     * |     if (isNaN(value)) {
     * |         throw new TypeError('value is not a number');
     * |     } else {
     * |         next();
     * |     }
     * | }).catch(function (reason) {
     * |     console.error(reason); // → TypeError: value is not a number;
     * | });
     *
     * 5. Break interations async:
     *
     * | new AsyncIterator([1, NaN, 3], function (value, index, array, next, finish, thrower) {
     * |     if (isNaN(value)) {
     * |         thrower(new TypeError('value is not a number'));
     * |     } else {
     * |         next();
     * |     }
     * | }).catch(function (reason) {
     * |     console.error(reason); // → TypeError: value is not a number;
     * | });
     *
     * @constructor
     * @param {ArrayLikeObject} iterable
     * @param {Function} callback – function (value:any, index:number, iterable:ArrayLikeObject, next:Function, finish:Function, thrower:Function) {…}
     * @param {Object} [thisObject]
     * @returns {Promise}
     */
    function AsyncIterator(iterable, callback, thisObject) {
        if (!(this instanceof AsyncIterator)) {
            throw new TypeError('The AsyncIterator constructor requires "new"');
        }

        this._iterable = iterable;
        this._callback = callback;

        if (thisObject === undefined) {
            this._callback = callback;
        } else {
            this._callback = callback.bind(thisObject);
        }

        this._index = -1;
        this._done = false;

        var iterator = this;
        var promise = new Promise(function (resolve, reject) {
            iterator._resolve = resolve;
            iterator._reject = reject;
        });

        this._onIteration = this._onIteration.bind(this);
        this._next = this._next.bind(this);
        this._finish = this._finish.bind(this);
        this._thrower = this._thrower.bind(this);

        this._next();

        return promise;
    }

    AsyncIterator.prototype = {
        constructor: AsyncIterator,

        /**
         * @type {number}
         */
        _index: -1,

        /**
         * @type {boolean}
         */
        _done: false,

        /**
         * @type {ArrayLikeObject}
         */
        _iterable: null,

        /**
         * @type {Function}
         */
        _callback: null,

        /**
         * @type {Function}
         */
        _resolve: null,

        /**
         * @type {Function}
         */
        _reject: null,

        _onIteration: function () {
            var index = this._index;
            var iterable = this._iterable;
            var value = iterable[index];
            var callback = this._callback;

            callback(value, index, iterable, this._next, this._finish, this._thrower);
        },

        /**
         * @param {any} value
         */
        _next: function (value) {
            if (!this._done) {
                this._index++;

                if (this._index < this._iterable.length) {
                    requestMicrotask().then(this._onIteration).catch(this._thrower);
                } else {
                    this._finish(value);
                }
            }
        },

        /**
         * @param {any} value
         */
        _finish: function (value) {
            if (!this._done) {
                this._done = true;
                this._resolve(value);
                this._destroy();
            }
        },

        /**
         * @param {Error} reason
         */
        _thrower: function (reason) {
            if (!this._done) {
                this._done = true;
                this._reject(reason);
                this._destroy();
            }
        },

        _destroy: function () {
            this._resolve = null;
            this._reject = null;
            this._iterable = null;
            this._callback = null;
            this._onIteration = null;
            this._next = null;
            this._finish = null;
            this._thrower = null;
        },
    };

    module.exports = AsyncIterator;
});
